import React, { useState, useEffect } from "react";
import { Turnstile } from "@marsidev/react-turnstile";
import { FaCheck } from "react-icons/fa";
import { SignInLink } from "../utility/routeUtils";
import SignupFormFields from "./Shared/SignupFormFields";
import { supabase } from "../supabaseClient";
import { validatePassword } from "../utils/validation";
import {
  sanitizeInput,
  sanitizeEmail,
  sanitizePhone,
} from "../utils/sanitization";
import { collectSignupFieldErrors } from "../utility/signupFormValidation";

const SignUpForm = () => {
  const [formData, setFormData] = useState({
    displayName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [verificationEmail, setVerificationEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [passwordChecks, setPasswordChecks] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let sanitizedValue = value;

    // Apply appropriate sanitization based on field type
    switch (name) {
      case "email":
        sanitizedValue = sanitizeEmail(value);
        break;
      case "phone":
        sanitizedValue = sanitizePhone(value);
        break;
      case "displayName":
        sanitizedValue = sanitizeInput(value);
        break;
      default:
        sanitizedValue = value;
    }

    // Update form data with sanitized value
    if (sanitizedValue !== value) {
      setFormData((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));
    }
  };

  useEffect(() => {
    if (!formData.password) return;
    const passwordValidation = validatePassword(formData.password);
    if (passwordValidation?.checks) {
      setPasswordChecks(passwordValidation.checks);
    }
  }, [formData.password]);

  const validateForm = () => {
    const newErrors = collectSignupFieldErrors(formData, {
      showTerms: true,
      acceptedTerms,
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCaptchaError = () => {
    setCaptchaToken("");
    setErrors((prev) => ({
      ...prev,
      submit: "Captcha verification failed. Please try again.",
    }));
  };

  const handleCaptchaExpire = () => {
    setCaptchaToken("");
  };

  const handleCaptchaVerify = (token) => {
    setCaptchaToken(token);
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.submit;
      return newErrors;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSuccess("");
    setLoading(true);

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    if (!captchaToken) {
      setErrors({
        submit: "Please complete the captcha verification",
      });
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.auth.signUp({
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        options: {
          data: {
            display_name: formData.displayName.trim(),
            phone_number: formData.phone.trim() || "",
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        throw error;
      }

      if (data.user) {
        setVerificationEmail(formData.email);
        setVerificationSent(true);
        setSuccess(
          "Account created successfully! Please check your email to verify your account."
        );
      }
    } catch (error) {
      console.error("Signup error:", error);
      setErrors({
        submit: error.message || "Failed to create account. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: verificationEmail,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        throw error;
      }

      setSuccess("Verification email sent! Please check your inbox.");
    } catch (error) {
      console.error("Resend verification error:", error);
      setErrors({
        submit: error.message || "Failed to resend verification email.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (verificationSent) {
    return (
      <section className="min-h-screen bg-brand-cream flex flex-col justify-center py-8 px-4 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h1 className="mt-6 text-center heading-display text-3xl">
            Check your email
          </h1>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="card-board py-8 px-4 sm:px-10">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-lg bg-brand-charcoal">
                <svg
                  className="h-6 w-6 text-brand-cream"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                Verify your email address
              </h3>
              <p className="mt-2 text-sm text-gray-500">
                We've sent a verification link to{" "}
                <span className="font-medium text-gray-900">
                  {verificationEmail}
                </span>
              </p>
              <p className="mt-2 text-sm text-gray-500">
                Please check your email and click the link to verify your
                account.
              </p>
              <div className="mt-6 space-y-4">
                <p className="text-sm text-gray-500">
                  Didn't receive the email?{" "}
                  <button
                    onClick={handleResendVerification}
                    className="font-medium text-brand-gold-ink hover:text-brand-charcoal"
                  >
                    Click here to resend
                  </button>
                </p>
                <p className="text-sm text-gray-500">
                  Already confirmed your email?{" "}
                  <SignInLink
                    to="/signin"
                    className="font-medium text-brand-gold-ink hover:text-brand-charcoal"
                  >
                    Sign in here
                  </SignInLink>
                </p>
              </div>
              {success && (
                <div className="mt-4 rounded-lg card-inset p-4">
                  <div className="text-sm text-brand-charcoal">{success}</div>
                </div>
              )}
              {errors.submit && (
                <div className="mt-4 rounded-md bg-red-50 p-4">
                  <div className="text-sm text-red-700">{errors.submit}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen bg-brand-cream flex flex-col justify-center py-8 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h1 className="mt-6 text-center heading-display text-3xl">
          Create your account
        </h1>
        <p className="mt-2 text-center text-sm text-gray-600">
          Or{" "}
          <SignInLink
            to="/signin"
            className="font-medium text-brand-gold-ink hover:text-brand-charcoal"
          >
            sign in to your existing account
          </SignInLink>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="card-board py-8 px-4 sm:px-10">
          {errors.submit && (
            <div className="rounded-md bg-red-50 p-4 mb-6">
              <div className="text-sm text-red-700">{errors.submit}</div>
            </div>
          )}

          {success && (
            <div className="rounded-lg card-inset p-4 mb-6">
              <div className="text-sm text-brand-charcoal">{success}</div>
            </div>
          )}

          <SignupFormFields
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            setErrors={setErrors}
            loading={loading}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            showConfirmPassword={showConfirmPassword}
            setShowConfirmPassword={setShowConfirmPassword}
            passwordChecks={passwordChecks}
            setPasswordChecks={setPasswordChecks}
            onInputChange={handleChange}
            onInputBlur={handleBlur}
            onSubmit={handleSubmit}
            submitButtonText={
              loading
                ? "Creating account..."
                : verificationSent
                ? "Verification email sent"
                : "Create account"
            }
            submitButtonIcon={<FaCheck className="mr-2" />}
            showTerms={true}
            acceptedTerms={acceptedTerms}
            setAcceptedTerms={setAcceptedTerms}
          >
            <div className="flex justify-center">
              <Turnstile
                siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                onSuccess={handleCaptchaVerify}
                onError={handleCaptchaError}
                onExpire={handleCaptchaExpire}
              />
            </div>
          </SignupFormFields>
        </div>
      </div>
    </section>
  );
};

export default SignUpForm;
